/**
 * Support Assistant chat — ephemeral UI state.
 *
 * Per product decision: no persisted history. The drawer opens empty every
 * page reload. Audit trail lives server-side in BigQuery; the UI is purely
 * a render surface.
 *
 * `sessionId` is keyed to the browser TAB (sessionStorage), so the user can
 * close + reopen the drawer mid-conversation without losing audit correlation.
 * Reload → new sessionId. "Nova conversa" button forces a new sessionId.
 */

import { create } from 'zustand';

export type SupportMessageRole = 'user' | 'assistant';

/** "yes" / "no" feedback on an assistant turn. null = no feedback yet. */
export type SupportFeedback = 'helpful' | 'not_helpful' | null;

export interface SupportMessage {
  id: string;
  role: SupportMessageRole;
  content: string;
  citations?: string[];
  refusalType?: string | null;
  softWarnings?: string[];
  isStreaming?: boolean;
  createdAt: number;
  /** Server-side request id for correlation with BigQuery audit. */
  requestId?: string;
  /** User's thumbs feedback on this assistant turn. */
  feedback?: SupportFeedback;
}

const SESSION_KEY = 'janus.support.sessionId';

function ensureSessionId(): string {
  if (typeof window === 'undefined') return cryptoRandomId();
  const existing = window.sessionStorage.getItem(SESSION_KEY);
  if (existing) return existing;
  const fresh = cryptoRandomId();
  window.sessionStorage.setItem(SESSION_KEY, fresh);
  return fresh;
}

function cryptoRandomId(): string {
  try {
    return (crypto as Crypto).randomUUID();
  } catch {
    return 'sup-' + Math.random().toString(36).slice(2) + Date.now().toString(36);
  }
}

interface SupportAssistantState {
  isOpen: boolean;
  sessionId: string;
  messages: SupportMessage[];
  isSending: boolean;
  errorMessage: string | null;
  /**
   * One-shot text to seed the composer with the next time the drawer
   * renders. Used by the Janus Agent widget hand-off so the assistant
   * opens pre-filled with the user's question. The widget consumes and
   * clears it via `consumePrefill()` on open.
   */
  prefillDraft: string | null;

  open: () => void;
  close: () => void;
  toggle: () => void;

  /** Queue text to pre-fill the composer on next open (does not send). */
  setPrefillDraft: (text: string) => void;
  /** Read + clear the queued pre-fill text (returns null when empty). */
  consumePrefill: () => string | null;

  /** Start a brand-new session (clears messages + rotates sessionId). */
  resetConversation: () => void;

  /** Optimistically push the user's message; returns its id. */
  appendUserMessage: (content: string) => string;

  /** Push a placeholder assistant message (used for streaming). */
  appendAssistantPlaceholder: () => string;

  /** Patch the assistant message identified by `id`. */
  patchAssistantMessage: (id: string, patch: Partial<SupportMessage>) => void;

  /** Append a streamed token chunk to the assistant message. */
  appendAssistantChunk: (id: string, chunk: string) => void;

  /** Set the user's thumb feedback on an assistant message. */
  setFeedback: (id: string, value: SupportFeedback) => void;

  setSending: (sending: boolean) => void;
  setError: (msg: string | null) => void;
}

export const useSupportAssistantStore = create<SupportAssistantState>((set, get) => ({
  isOpen: false,
  sessionId: ensureSessionId(),
  messages: [],
  isSending: false,
  errorMessage: null,
  prefillDraft: null,

  open: () => set({ isOpen: true, errorMessage: null }),
  close: () => set({ isOpen: false }),
  toggle: () => set((s) => ({ isOpen: !s.isOpen })),

  setPrefillDraft: (text) => set({ prefillDraft: text }),
  consumePrefill: () => {
    const { prefillDraft } = get();
    if (prefillDraft != null) set({ prefillDraft: null });
    return prefillDraft;
  },

  resetConversation: () => {
    const next = cryptoRandomId();
    try {
      window.sessionStorage.setItem(SESSION_KEY, next);
    } catch {
      /* no-op */
    }
    set({ sessionId: next, messages: [], errorMessage: null, isSending: false });
  },

  appendUserMessage: (content) => {
    const id = cryptoRandomId();
    set((s) => ({
      messages: [
        ...s.messages,
        { id, role: 'user', content, createdAt: Date.now() },
      ],
    }));
    return id;
  },

  appendAssistantPlaceholder: () => {
    const id = cryptoRandomId();
    set((s) => ({
      messages: [
        ...s.messages,
        { id, role: 'assistant', content: '', isStreaming: true, createdAt: Date.now(), feedback: null },
      ],
    }));
    return id;
  },

  patchAssistantMessage: (id, patch) => {
    set((s) => ({
      messages: s.messages.map((m) => (m.id === id ? { ...m, ...patch } : m)),
    }));
  },

  appendAssistantChunk: (id, chunk) => {
    set((s) => ({
      messages: s.messages.map((m) =>
        m.id === id ? { ...m, content: m.content + chunk } : m,
      ),
    }));
  },

  setFeedback: (id, value) => {
    set((s) => ({
      messages: s.messages.map((m) => (m.id === id ? { ...m, feedback: value } : m)),
    }));
  },

  setSending: (sending) => set({ isSending: sending }),
  setError: (msg) => set({ errorMessage: msg, isSending: false }),
}));
